import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "@zorni/components-lib";

import styles from "./TravelerForm.module.css";

const CATEGORY_LABELS = {
  ADULT: ["Traveler.Content.Adult", "Adulte"],
  CHILD: ["Traveler.Content.Child", "Enfant"],
  BABY: ["Traveler.Content.Baby", "Bébé"],
};

/**
 * Saisie des voyageurs de la réservation — « 04 - Information ».
 * Une carte par voyageur : prénom, nom, date de naissance. Les erreurs
 * (champ manquant, âge hors catégorie) s'affichent sous la carte concernée.
 */
const TravelerForm = ({
  travelers = [],
  onChange,
  ageHints = {},
  errors = [],
  showErrors = false,
  onReclassify,
  profile = null,
  onUseProfile,
}) => {
  const { t } = useTranslation();

  const errorOf = (index) => errors.find((e) => e.index === index);

  const same = (a, b) => String(a ?? "").trim() === String(b ?? "").trim();
  const filledFromProfile = (traveler) =>
    Boolean(profile) &&
    same(traveler?.firstName, profile.firstName) &&
    same(traveler?.lastName, profile.lastName) &&
    (!profile.birthDate || same(traveler?.birthDate, profile.birthDate));

  const missingOf = (traveler) =>
    [
      !traveler.firstName?.trim() && t("booking.travelers.firstName", "prénom"),
      !traveler.lastName?.trim() && t("booking.travelers.lastName", "nom"),
      !traveler.birthDate && t("booking.travelers.birthDate", "date de naissance"),
    ].filter(Boolean);

  const field = (traveler, index, key, label, type = "text", placeholder = "") => (
    <label className={styles.field}>
      <span className={styles.label}>
        {label} <span aria-hidden="true">*</span>
      </span>
      <input
        type={type}
        value={traveler[key] ?? ""}
        placeholder={placeholder}
        onChange={(event) => onChange(index, key, event.target.value)}
        required
      />
    </label>
  );

  return (
    <div className={styles.list}>
      {travelers.map((traveler, index) => {
        const [key, fallback] = CATEGORY_LABELS[traveler.ageCategory] ?? CATEGORY_LABELS.ADULT;
        const ageError = errorOf(index);
        const missing = missingOf(traveler);
        const invalid = showErrors && (missing.length > 0 || !!ageError);
        const hint = ageHints[traveler.ageCategory];

        return (
          <article
            key={`${traveler.ageCategory}-${index}`}
            className={`${styles.card} ${invalid ? styles.invalid : ""}`}
            data-invalid={invalid ? "true" : undefined}
          >
            <header className={styles.head}>
              <span className={styles.avatar}>{index + 1}</span>
              <strong>{t("booking.travelers.one", { count: index + 1, defaultValue: `Voyageur ${index + 1}` })}</strong>
              <span className={styles.tag}>{t(key, fallback)}</span>
              {traveler.isMainPerson && (
                <span className={`${styles.tag} ${styles.tagMe}`}>
                  {t("booking.travelers.you", "C'est vous")}
                </span>
              )}

              {/* Le voyageur principal, c'est le titulaire du compte : on lui
                  évite de ressaisir ce que le profil contient déjà. */}
              {traveler.isMainPerson && profile && onUseProfile && (
                <button
                  type="button"
                  className={styles.fillFromProfile}
                  onClick={() => onUseProfile(index)}
                  disabled={filledFromProfile(traveler)}
                >
                  <Icon name="iconUsers" size="sm" />
                  {filledFromProfile(traveler)
                    ? t("booking.travelers.profileUsed", "Coordonnées reprises du profil")
                    : t("booking.travelers.useProfile", "Utiliser mes coordonnées")}
                </button>
              )}
            </header>

            <div className={styles.fields}>
              {field(traveler, index, "firstName", t("booking.travelers.firstNameLabel", "Prénom"))}
              {field(traveler, index, "lastName", t("booking.travelers.lastNameLabel", "Nom"))}
              <div className={styles.field}>
                {field(traveler, index, "birthDate", t("booking.travelers.birthDateLabel", "Date de naissance"), "date")}
                {hint && <span className={styles.hint}>{hint}</span>}
              </div>
            </div>

            {invalid && (
              <p className={styles.error}>
                <Icon name="iconWarning" size="sm" />
                <span>
                  {ageError
                    ? ageError.message
                    : t("booking.travelers.missing", {
                        fields: missing.join(", "),
                        defaultValue: `Complétez : ${missing.join(", ")}.`,
                      })}
                </span>
              </p>
            )}

            {invalid && ageError?.suggestedAgeCategory && onReclassify && (
              <div className={styles.fix}>
                <Button
                  variant="secondary"
                  size="small"
                  label={ageError.suggestedActionLabel}
                  onClick={() => onReclassify(index, ageError.suggestedAgeCategory)}
                />
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
};

TravelerForm.propTypes = {
  travelers: PropTypes.array,
  profile: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    birthDate: PropTypes.string,
  }),
  onUseProfile: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  ageHints: PropTypes.object,
  errors: PropTypes.array,
  showErrors: PropTypes.bool,
  onReclassify: PropTypes.func,
};

export default TravelerForm;
